import { AxiosError } from "axios";
import { isEqual } from "lodash";
import * as moment from "moment";

import { toast } from "@teamprevent/common/toasts";

import { Location } from "redux-little-router";

const CopyableError: string = "copyableError";
const SeverityWarn: string = "warn";
const SeverityInfo: string = "info";
const SeverityError: string = "error";

export interface Message {
    messageText: string;
    severityLevel: string;
}

export interface ResponseWrapper<T> {
    success: boolean;
    messages: Message[];
    data: T;
}

// A type whose date or moment properties are replaced as strings (which is how they are sent to and from the server)
export type ServerModelWithDateStrings<T> = T extends undefined ? undefined : T extends Date | moment.Moment ? string : {
    [K in keyof T]: ServerModelWithDateStrings<T[K]>;
}

export const GENDER_OPTIONS = [
    "Male",
    "Female",
    "Non-binary",
    "Prefer not to say",
    "Other"
].map(o => ({ text: o, value: o }));

export default class Utils {

    public static GetRelevantOrgStructureId(location: Location): any {
        return location.params.orgStructureFourId ||
            location.params.orgStructureThreeId ||
            location.params.orgStructureTwoId ||
            location.params.orgStructureOneId;
    }

    public static DateTimeToString(value: moment.Moment | string): string {

        if (typeof value === "string") {
            value = moment(value);
        }

        if (null ===  value || undefined ===  value)  {
            return "";
        }
        return value.format("LLL");
    }

    public static DateToString(value: moment.Moment | string): string {

        if (typeof value === "string") {
            value = moment(value);
        }

        if (null === value || undefined === value) {
            return "";
        }
        return value.format("LL");
    }

    public static BoolToString(value: boolean): string {
        if (null === value || undefined === value) {
            return "";
        }

        return value.toString();
    }

    public static CheckException(ex: any) {
        const axiosError = ex as AxiosError<string>;
        if (axiosError?.response?.status === 400) {
            throw new Error(axiosError.response.data);
        }
    }

    public static CheckResponse<T>(response: ResponseWrapper<T>): T {
        let messages = response.messages;

        const copyableErrorMessages = response.messages.filter(m => m.severityLevel === CopyableError);
        if (copyableErrorMessages.length > 0) {
            toast.copyableError(copyableErrorMessages.map(cem => cem.messageText));
            messages = messages.filter(m => m.severityLevel !== CopyableError);
        }

        messages.forEach((element: Message) => {
            switch (element.severityLevel) {
                case SeverityWarn:
                    toast.warning(element.messageText);
                    break;
                case SeverityInfo:
                    toast.info(element.messageText);
                    break;
                case SeverityError:
                default:
                    toast.error(element.messageText);
                    break;
            }
        });
        return response.data;
    }

    public static getParameters() { return window.location.href; }

    public static getOnlyParameters() {
        const startIndex = window.location.href.indexOf("?");
        if (!(startIndex > 0)) {
            return "";
        }
        return window.location.href.substring(window.location.href.indexOf("?"), window.location.href.length);
    }

    public static AddQueryStringParameter(queryString: string, itemName: string, itemValue: string): string {
        if (null == itemName || null == itemValue) { // eslint-disable-line eqeqeq
            return queryString;
        }
        const queryStringIndex = queryString.indexOf("?");
        let returnString = "?";
        if (queryStringIndex >= 1) {
            returnString = queryString;
        }
        returnString = `${returnString}&${itemName}=${itemValue}`;
        return returnString;
    }

    public static calculateAge(birthday: string): string {

        if (!birthday) {
            return "Not Known";
        }
        try {
            const dateParts = birthday.split("/");
            const birthDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
            const fullDateNow = new Date();
            const now = new Date(fullDateNow.getFullYear(), fullDateNow.getMonth(), fullDateNow.getDate());
            if (now === birthDate) {
                return "0";
            }
            let age = now.getFullYear() - birthDate.getFullYear();
            if (birthDate.getMonth() > now.getMonth() ||
                (birthDate.getMonth() === now.getMonth() && now.getDate() < birthDate.getDate())) {
                age--;
            }

            if (age < 0) {
                return "Not born yet!";
            }

            return age.toString();
        } catch {
            return "Error with date format";
        }
    }

    public static IsStringArraysContentEqual(array1: string[] | undefined, array2: string[] | undefined) {
        const sortedArray1 = (array1 ?? []).sort();
        const sortedArray2 = (array2 ?? []).sort();
        if (sortedArray1.length !== sortedArray2.length) {
            return false;
        }
        return isEqual(sortedArray1, sortedArray2);
    }
}
